import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  input,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'

import { clearNode, createDomain, sample, withRegion } from 'effector'
import { fabric } from 'fabric'
import { curry as _curry, differenceBy as _differenceBy } from 'lodash/fp'

import { BehaviorSubject, Subject, combineLatest, map } from 'rxjs'
import { filter, takeUntil, tap } from 'rxjs/operators'

import { FlxSpinnerComponent } from '@flowx/angular-ui-toolkit'
import { BpmnEditorNodeActionsComponent } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/bpmn-editor-node-actions.component'
import { BpmnInstanceActionsStore } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/store/bpmn-instance-actions.store'
import { BPMNNodeActionsEffects } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/store/bpmn-node-actions.effects'
import { BPMNNodeActionsEvents } from '@flowx/designer/modules/bpmn-editor/components/bpmn-editor-node-actions/store/bpmn-node-actions.events'
import { getSwimlanes, setReadonly } from '@flowx/designer/modules/bpmn-editor/fabric-graph.store'
import {
  CurrentNodeColors,
  FabricConstants,
  NodeAuditStyles,
  PendingBoundaryEventColors,
  PendingEdgeColors,
  PendingNodeColors,
  VisitedBoundaryEventColors,
  VisitedEdgeColors,
  VisitedNodeColors,
} from '@flowx/designer/modules/bpmn-editor/fabric.constants'
import { NgFabricService } from '@flowx/designer/modules/bpmn-editor/ng-fabric.service'
import {
  BoundaryEventRender,
  CanvasData,
  NodeDefinitionRender,
} from '@flowx/designer/modules/bpmn-editor/ng-fabric.types'
import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { BpmnEditorSamples } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.samples'
import { BpmnEditorStore } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.store'
import {
  NodeDefinition,
  NodeDefinitionNormalized,
  ProcessDefinition,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ProcessInstanceDetailsPanelComponent } from '@flowx/designer/modules/process/components/process-instance-details-panel/process-instance-details-panel.component'
import { AppEvents } from '@flowx/designer/shared/effector/app/app.events'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import { BoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/boundary-event.types'
import { EventNodes, GatewayNodes } from '@flowx/designer/shared/entities/node.types'
import { ProcessDefinitionEntity } from '@flowx/designer/shared/entities/process-definition.entity'
import {
  ApplicationProcessData,
  NodeActionState,
  ProcessDefinitionForInstance,
  ProcessInstance,
  ProcessInstanceBreadcrumb,
} from '@flowx/designer/shared/entities/process-instance.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'
import { TOKEN_STATE } from '@flowx/shared/interfaces'

@Component({
  selector: 'app-process-instance',
  templateUrl: './process-instance.component.html',
  providers: [BpmnInstanceActionsStore],
  styleUrls: ['./process-instance.component.scss'],
  imports: [
    CommonModule,
    BpmnEditorNodeActionsComponent,
    FlxSpinnerComponent,
    ProcessInstanceDetailsPanelComponent,
  ],
})
export class ProcessInstanceComponent implements OnInit, AfterViewInit, OnDestroy {
  #destroy$ = new Subject<void>()
  #destroyRef = inject(DestroyRef)
  #embedId: string
  #embedContext: string
  #route = inject(ActivatedRoute)
  #processInstanceService = inject(ProcessInstanceService)
  #fabricService = inject(NgFabricService)
  // Ensures effector samples connecting ProcessEffector.loadProcess → SequenceStores are initialized do not delete
  readonly #bpmnEditorSamples = inject(BpmnEditorSamples)
  readonly #bpmnEditorStore = inject(BpmnEditorStore)

  readonly #Effector = createDomain('ProcessInstanceComponent')

  #applicationProcessData$ = new BehaviorSubject<ApplicationProcessData>(null)
  #canvasParentResizeObserver: ResizeObserver = null

  @Input() set applicationProcessData(applicationProcessData: ApplicationProcessData) {
    this.#applicationProcessData$.next(applicationProcessData)
  }

  get applicationProcessData(): ApplicationProcessData {
    return this.#applicationProcessData$.value
  }

  @Input() loading: boolean

  @Output() navigateToSubProcess = new EventEmitter()
  @Output() backToParentProcess = new EventEmitter()
  @Output() navigateToEmbeddedProcess = new EventEmitter()

  @ViewChild('graphWrapper') graphWrapper: ElementRef
  definitionsExist = input(false)
  showBreadcrumb = input(true)
  canvas: fabric.Canvas

  readonly renderGraphFx = this.#Effector.createEffect<void, void>('renderGraphFx').use(() => {
    this.renderGraph()
  })
  readonly BpmnEditorStores = BpmnEditorStores

  detailsPanelHeight = FabricConstants.OptionsPanelHeightPc

  breadcrumbs: ProcessInstanceBreadcrumb[] = []

  bpmnInstanceActionsStore = inject(BpmnInstanceActionsStore)

  constructor() {
    BPMNNodeActionsEffects.navToEmbeddedInstanceFx.use(this.onNavigateToEmbeddedProcess.bind(this))
  }

  ngOnInit(): void {
    AppEvents.processInstancePageInit()
    this.#route.queryParamMap
      .pipe(
        map((queryParamMap) => ({
          breadcrumbs: queryParamMap.get('breadcrumbs'),
          embedId: queryParamMap.get('embedId'),
          embedContext: queryParamMap.get('context'),
        })),
        tap(({ breadcrumbs, embedId, embedContext }) => {
          this.breadcrumbs = breadcrumbs ? JSON.parse(breadcrumbs) : []

          this.#embedId = embedId
          this.#embedContext = embedContext
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()

    withRegion(this.#Effector, () => {
      sample({
        clock: BPMNNodeActionsEvents.navToEmbeddedInstance,
        source: {
          node: BpmnEditorStores.selectedNode$,
        },
        fn: ({ node }) => ({ node }),
        target: BPMNNodeActionsEffects.navToEmbeddedInstanceFx,
      })
    })
  }

  ngAfterViewInit(): void {
    const wrapperHeight = this.graphWrapper.nativeElement.offsetHeight
    const wrapperWidth = this.graphWrapper.nativeElement.offsetWidth

    this.canvas = new fabric.Canvas('diagram', {
      height: wrapperHeight,
      width: wrapperWidth,
      fireRightClick: true,
      stopContextMenu: true,
      stateful: true,
      perPixelTargetFind: true,
      targetFindTolerance: 36,
      preserveObjectStacking: true,
    })

    this.#updateCanvasDimensionsWhenParentChanges()

    BpmnEditorEvents.canvasReady(this.canvas)

    this.#fabricService.init({ canvas: this.canvas })

    combineLatest([
      this.#applicationProcessData$,
      this.#processInstanceService.processInstanceLoading,
    ])
      .pipe(
        filter(
          ([applicationProcessData, loading]) =>
            applicationProcessData?.processData?.definition && !loading
        ),
        tap(([applicationProcessData]) => {
          BpmnEditorEvents.processVersionChanged(applicationProcessData.processData.definition.id)
          ProcessEffector.loadProcess(
            applicationProcessData.processData?.definition as ProcessDefinition
          )

          this.renderGraph()
        }),
        takeUntil(this.#destroy$) // Used instead of takeUntilDestroyed to unsubscribe before processInstanceLoading emits false when resetting the instance data.
      )
      .subscribe()
  }

  renderGraph(): void {
    if (!this.applicationProcessData?.processData) {
      return
    }
    const { definition, instance, swimlanes } = this.applicationProcessData.processData

    // visitedNodes/currentNodes are deduplicated by id — at most one entry per node definition.
    const { visited: visitedNodes, current: currentNodes } = this.getCurrentAndVisitedNodes(
      definition,
      instance
    )

    const visitedEdges = this.getVisitedSequences(instance.tokens, visitedNodes)

    const pendingNodes = _differenceBy('id', definition.nodes, [...visitedNodes, ...currentNodes])
    const pendingEdges = _differenceBy('id', definition.edges, visitedEdges)

    const boundaryEvents = ProcessDefinitionEntity.getBoundaryEventsFromProcess(
      definition as ProcessDefinition
    )

    const visitedBoundaryEvents = this.getVisitedBoundaryEvents(boundaryEvents, visitedEdges)

    const pendingBoundaryEvents = _differenceBy('id', boundaryEvents, visitedBoundaryEvents)

    const decorateNodeWithRenderInfo = _curry(
      (flxStylesUpdate: NodeAuditStyles, node: NodeDefinitionRender): NodeDefinitionRender => ({
        ...node,
        flxStyles: {
          ...node?.flxStyles,
          ...flxStylesUpdate,
        },
      })
    )

    type SequencePointsRender = SequencePoints & { flxStyles?: NodeAuditStyles }

    const decorateSequenceWithRenderInfo = _curry(
      (flxStylesUpdate: NodeAuditStyles, sequence: SequencePointsRender): SequencePointsRender => ({
        ...sequence,
        flxStyles: {
          ...sequence?.flxStyles,
          ...flxStylesUpdate,
        },
      })
    )

    const decorateBoundaryEventWithRenderInfo = _curry(
      (
        flxStylesUpdate: NodeAuditStyles,
        boundaryEvent: BoundaryEventRender
      ): BoundaryEventRender => ({
        ...boundaryEvent,
        flxStyles: {
          ...boundaryEvent?.flxStyles,
          ...flxStylesUpdate,
        },
      })
    )

    const decoratePendingNode = decorateNodeWithRenderInfo(PendingNodeColors)
    const decorateVisitedNode = decorateNodeWithRenderInfo(VisitedNodeColors)
    const decorateCurrentNode = decorateNodeWithRenderInfo(CurrentNodeColors)

    const decorateVisitedEdge = decorateSequenceWithRenderInfo(VisitedEdgeColors)
    const decoratePendingEdge = decorateSequenceWithRenderInfo(PendingEdgeColors)

    const decorateVisitedBoundaryEvent = decorateBoundaryEventWithRenderInfo(
      VisitedBoundaryEventColors
    )
    const decoratePendingBoundaryEvent = decorateBoundaryEventWithRenderInfo(
      PendingBoundaryEventColors
    )

    const data: CanvasData = {
      nodes: [
        ..._differenceBy('id', visitedNodes, currentNodes).map(decorateVisitedNode),
        ...currentNodes.map(decorateCurrentNode),
        ...pendingNodes.map(decoratePendingNode),
      ],
      sequences: [
        ...visitedEdges.map(decorateVisitedEdge),
        ...pendingEdges.map(decoratePendingEdge),
      ],
      swimlanes,
      boundaryEvents: [
        ...visitedBoundaryEvents.map(decorateVisitedBoundaryEvent),
        ...pendingBoundaryEvents.map(decoratePendingBoundaryEvent),
      ],
    }

    this.#fabricService.clear()
    setTimeout(() => {
      setReadonly(true)
      BpmnEditorEvents.onCanvasReadonly(true)
    }, 300)
    this.#fabricService.renderGraph(data)
    const _swimlanes = [...getSwimlanes().values()].map((swimlane) => swimlane.metadata)
    this.#fabricService.panToGraphCenter(
      { swimlanes: _swimlanes, canvas: this.canvas },
      {
        x: 0,
        y: this.canvas.height * -this.detailsPanelHeight,
      }
    )
  }

  /*
   * Computes the visited and current node sets for the instance.
   *
   * Linear in the total number of nodesActionStates:
   *   - node definitions are indexed by id once, so the per-state lookup is O(1);
   *   - the "are there remaining states for this context" checks are answered from
   *     precomputed suffix counts instead of re-slicing + filtering the tail on every
   *     iteration (which was O(N²) and the dominant cost on polling-loop instances);
   *   - results are deduplicated by id, since the same node def can be visited
   *     thousands of times in a loop and downstream consumers only need the unique set.
   */
  getCurrentAndVisitedNodes(
    definition: ProcessDefinitionForInstance,
    instanceData: ProcessInstance
  ): { visited: NodeDefinition[]; current: NodeDefinition[] } {
    // Index node definitions by id for O(1) lookup (keep first occurrence to match `find`)
    const nodeDefinitionsById = new Map<number, NodeDefinition>()
    definition.nodes.forEach((node) => {
      if (!nodeDefinitionsById.has(node.id)) {
        nodeDefinitionsById.set(node.id, node)
      }
    })

    const context = instanceData.embedContext
    const parentContext = instanceData.prevEmbedContext || instanceData.embedContext

    // Take all active tokens
    const activeTokens = instanceData.tokens.filter((token) =>
      [TOKEN_STATE.ACTIVE, TOKEN_STATE.INACTIVE, TOKEN_STATE.ON_HOLD].includes(token.state)
    )

    // Dedupe by id: a node can be visited many times in a loop, but the unique set is enough.
    const visitedById = new Map<number, NodeDefinition>()
    const currentById = new Map<number, NodeDefinition>()

    activeTokens.forEach((token) => {
      const states = token.nodesActionStates
      const length = states.length

      // Suffix counts computed once in O(N): remainingForContext[i] is the number of states
      // *after* index i whose context matches. This replaces the per-iteration slice + filter.
      const remainingForContext = new Array<number>(length + 1).fill(0)
      const remainingForParentContext = new Array<number>(length + 1).fill(0)
      for (let i = length - 1; i >= 0; i--) {
        remainingForContext[i] =
          remainingForContext[i + 1] + (states[i].context === context ? 1 : 0)
        remainingForParentContext[i] =
          remainingForParentContext[i + 1] + (states[i].context === parentContext ? 1 : 0)
      }

      states.forEach((nodeActionState, i) => {
        const foundNode =
          nodeActionState.context === context
            ? nodeDefinitionsById.get(nodeActionState.nodeId)
            : undefined
        if (foundNode) {
          visitedById.set(foundNode.id, foundNode)

          const notEndEventAndGatewayParallelType =
            foundNode.type !== EventNodes.END_EVENT &&
            foundNode.type !== GatewayNodes.GATEWAY_PARALLEL

          const isEndEventOrGatewayParallelTypeAndTokenNotInactive =
            (foundNode.type === EventNodes.END_EVENT ||
              foundNode.type === GatewayNodes.GATEWAY_PARALLEL) &&
            token.state !== TOKEN_STATE.INACTIVE

          if (
            remainingForContext[i + 1] === 0 &&
            (notEndEventAndGatewayParallelType ||
              isEndEventOrGatewayParallelTypeAndTokenNotInactive) &&
            !(nodeActionState.embedNodeId && remainingForParentContext[i + 1] > 0)
          ) {
            currentById.set(foundNode.id, foundNode)
          }
        }
      })
    })

    return { visited: [...visitedById.values()], current: [...currentById.values()] }
  }

  getVisitedBoundaryEvents(
    boundaryEvents: BoundaryEvent[],
    visitedEdges: SequencePoints[]
  ): BoundaryEvent[] {
    return boundaryEvents.filter((boundaryEvent) =>
      visitedEdges.some((sequence) => sequence.boundaryEventId === boundaryEvent.id)
    )
  }

  getVisitedSequences(
    tokens: ProcessInstance['tokens'],
    visitedNodes: NodeDefinition[]
  ): SequencePoints[] {
    /* An edge is visited if its target node immediately follows its source node in the token -> nodeActionStates array */

    // flatMap avoids the O(V²) accumulator spread; getPreviousNode is resolved once per node
    // (it depends only on the node, not the sequence) instead of once per incoming sequence.
    return visitedNodes.flatMap((node) => {
      const previousNode = this.getPreviousNode(tokens, node)
      return node.incomingSequences.filter((sequence) =>
        sequence.boundaryEventId
          ? sequence.boundaryEventId === previousNode.triggeredByBoundaryEventId
          : sequence.startNodeId === previousNode.nodeId
      )
    })
  }

  getPreviousNode(tokens: ProcessInstance['tokens'], node: NodeDefinition): NodeActionState {
    let foundNode: NodeActionState
    tokens.some((token) => {
      const foundIndex = token.nodesActionStates.findIndex((_node) => _node.nodeId === node.id)
      if (foundIndex === 0) {
        foundNode = token.nodesActionStates[foundIndex]
      }
      if (foundIndex > 0) {
        foundNode = token.nodesActionStates[foundIndex - 1]
      }
    })
    return foundNode
  }

  onSubprocessNavigation(subprocessUuid: string, name: string, uuid: string): void {
    // if i'm navigating from an embed to another embed, i need to add the current embed to the breadcrumbs
    const _embedId = this.#embedId ?? null
    const _embedContext = this.#embedContext ?? null

    this.navigateToSubProcess.emit({
      subprocessUuid,
      name,
      uuid,
      ...(_embedId && { previousEmbedId: _embedId }),
      ...(_embedContext && { previousEmbedContext: _embedContext }),
      breadcrumbs: this.breadcrumbs,
    })
  }

  goBackToParent(): void {
    this.backToParentProcess.emit({ breadcrumbs: this.breadcrumbs })
  }

  onNavigateToEmbeddedProcess({ node }: { node: NodeDefinitionNormalized }): void {
    const subprocessUuid = this.applicationProcessData.processData?.instance?.uuid
    if (!subprocessUuid) {
      return
    }
    // if i'm navigating from an embed to another embed, i need to add the current embed to the breadcrumbs
    const _embedId = this.#embedId ?? null
    const _embedContext = this.#embedContext ?? null

    this.navigateToEmbeddedProcess.emit({
      embedId: node.id,
      context: this.applicationProcessData?.processData?.instance?.embedContext,
      ...(_embedId && { previousEmbedId: _embedId }),
      ...(_embedContext && { previousEmbedContext: _embedContext }),
      name: this.applicationProcessData?.processData?.definition.resourceName,
      uuid: subprocessUuid,
      subprocessUuid,
      breadcrumbs: this.breadcrumbs,
    })
  }

  ngOnDestroy(): void {
    this.#destroy$.next()
    this.#destroy$.complete()
    this.#fabricService.clear()
    clearNode(this.#Effector)
    AppEvents.processInstancePageDestroy()
    this.#processInstanceService.resetInstanceData()
    this.#canvasParentResizeObserver?.disconnect()
  }

  #updateCanvasDimensionsWhenParentChanges(): void {
    this.#canvasParentResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.canvas?.setDimensions({
          height: entry.contentRect.height,
          width: entry.contentRect.width,
        })
      }
    })

    this.#canvasParentResizeObserver.observe(this.graphWrapper.nativeElement)
  }
}
